import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Award, Banknote, BookOpen, Briefcase, Building2,
  CheckCircle, ChevronRight, GraduationCap, Heart,
  Lightbulb, Rocket, Shield, Star, Target,
  Users, Zap
} from 'lucide-react';

/* ── animated counter ───────────────────────────────────── */
const Counter = ({ to, suffix = '' }: { to: number; suffix?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(to / 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= to) { setCount(to); clearInterval(timer); }
      else setCount(start);
    }, 20);
    return () => clearInterval(timer);
  }, [inView, to]);
  return <span ref={ref}>{count}{suffix}</span>;
};

/* ── fade-up wrapper ────────────────────────────────────── */
const FadeUp = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ── data ───────────────────────────────────────────────── */
const pillars = [
  {
    icon: <GraduationCap size={36} />,
    label: 'Education Institute',
    headline: 'Learn. Build. Get Hired.',
    desc: 'India\'s first IT training institute with 50% Pay After Placement across 2 Gen AI industry-ready programs.',
    stat: '98%',
    statLabel: 'Placement Rate',
    color: '#4e3a69',
    accent: '#F0B429',
    link: '/courses',
  },
  {
    icon: <Briefcase size={36} />,
    label: 'HR Consulting',
    headline: 'Assess. Screen. Hire.',
    desc: 'End-to-end fresher hiring solutions — technical assessments, campus drives, mock interview panels, and talent acquisition for IT teams.',
    stat: '60',
    statLabel: 'Days to Job-Ready',
    color: '#2b2240',
    accent: '#4e3a69',
    link: '/hr-consulting',
  },
];

const differentiators = [
  { icon: <Banknote size={22} />, title: '50% Pay After Placement', desc: 'Pay only 50% upfront. The remaining 50% is due only after you receive your first job offer letter.' },
  { icon: <Zap size={22} />, title: '60-Day Intensive Programs', desc: 'Structured, fast-track curricula designed to make you job-ready in exactly 60 days.' },
  { icon: <Building2 size={22} />, title: 'Strong Hiring Network', desc: 'Our placement team actively connects students with vetted companies across product, services and startups.' },
  { icon: <Shield size={22} />, title: 'Zero Financial Risk', desc: 'No job offer means you owe nothing more. We stake our revenue on your success.' },
  { icon: <Award size={22} />, title: 'Placement Till First Offer', desc: 'We don\'t stop after training ends. We work with you until you have an accepted offer in hand.' },
];

const values = [
  { icon: <Lightbulb size={20} />, title: 'Innovation', desc: 'We lead with AI-integrated curricula and adopt new tech before it becomes mainstream.' },
  { icon: <Heart size={20} />, title: 'Student-First', desc: 'Every decision — from payment model to training structure — is built around student success.' },
  { icon: <Star size={20} />, title: 'Excellence', desc: 'Industry-standard training, real projects, and rigorous assessments in every program.' },
  { icon: <Users size={20} />, title: 'Community', desc: 'An ever-growing alumni network of placed professionals who give back to incoming batches.' },
  { icon: <Target size={20} />, title: 'Accountability', desc: "We tie our income to your outcome — that's how serious we are about your placement." },
  { icon: <Rocket size={20} />, title: 'Speed', desc: '60 days from zero to job-ready. No fluff, no filler — just high-impact skill building.' },
];

const stats = [
  { value: 2, suffix: '', label: 'Industry Programs' },
  { value: 60, suffix: ' Days', label: 'To Job-Ready' },
];

/* ── component ──────────────────────────────────────────── */
const About = () => {
  return (
    <div style={{ background: '#f7f6fb', minHeight: '100vh' }}>

      {/* ── HERO ─────────────────────────────────────────── */}
      <section style={{
        background: 'linear-gradient(135deg, #0d0b14 0%, #2b2240 55%, #1a1030 100%)',
        minHeight: '92vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: '120px 24px 80px',
      }}>
        {/* Background orbs */}
        {[
          { w: 520, h: 520, top: '-15%', left: '-10%', color: 'rgba(78,58,105,0.35)' },
          { w: 400, h: 400, bottom: '-10%', right: '-8%', color: 'rgba(240,180,41,0.1)' },
          { w: 300, h: 300, top: '30%', right: '20%', color: 'rgba(255,101,53,0.08)' },
        ].map((o, i) => (
          <motion.div
            key={i}
            style={{ position: 'absolute', width: o.w, height: o.h, borderRadius: '50%', background: o.color, filter: 'blur(80px)', top: (o as any).top, left: (o as any).left, bottom: (o as any).bottom, right: (o as any).right, pointerEvents: 'none' }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 7 + i * 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}

        {/* Grid overlay */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.3)', borderRadius: '50px', padding: '6px 20px', marginBottom: '28px', color: '#F0B429', fontSize: '0.82rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            <motion.span animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              <Zap size={12} style={{ fill: '#F0B429' }} />
            </motion.span>
            India's First 50% Pay After Placement Institute
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            style={{ fontSize: 'clamp(2.2rem, 6vw, 4.2rem)', fontWeight: '900', color: 'white', lineHeight: 1.1, marginBottom: '24px', letterSpacing: '-0.02em' }}
          >
            We Exist to Change{' '}
            <span style={{ background: 'linear-gradient(135deg, #F0B429 30%, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              How India Learns Tech
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, marginBottom: '48px', maxWidth: '680px', margin: '0 auto 48px' }}
          >
            Vista Tech is <strong style={{ color: 'rgba(255,255,255,0.9)' }}>India's first IT training institute</strong> to offer <strong style={{ color: 'rgba(255,255,255,0.9)' }}>50% Pay After Placement</strong> — making quality tech education truly risk-free.
          </motion.p>

          {/* Stat pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '52px' }}
          >
            {[
              { val: '60 Days', lbl: 'To Job-Ready' },
              { val: '98%', lbl: 'Placement Rate' },
            ].map((p) => (
              <div key={p.lbl} style={{ padding: '10px 20px', borderRadius: '50px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)', backdropFilter: 'blur(10px)' }}>
                <span style={{ color: '#F0B429', fontWeight: '800', fontSize: '0.95rem' }}>{p.val}</span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginLeft: '6px' }}>{p.lbl}</span>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <Link to="/courses" style={{ padding: '14px 32px', borderRadius: '12px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: 'white', fontWeight: '700', textDecoration: 'none', fontSize: '0.95rem', boxShadow: '0 8px 28px rgba(240,180,41,0.35)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              Explore Courses <ChevronRight size={17} />
            </Link>
            <Link to="/contact" style={{ padding: '14px 32px', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.85)', fontWeight: '600', textDecoration: 'none', fontSize: '0.95rem', background: 'rgba(255,255,255,0.05)' }}>
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── OUR STORY ────────────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '64px', alignItems: 'center' }}>
          {/* Visual card */}
          <FadeUp>
            <div style={{ position: 'relative' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2b2240, #4e3a69)',
                borderRadius: '24px',
                padding: '48px 40px',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 32px 80px rgba(43,34,64,0.3)',
              }}>
                {/* Decorative ring */}
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', borderRadius: '50%', border: '2px solid rgba(240,180,41,0.2)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '160px', height: '160px', borderRadius: '50%', border: '2px solid rgba(240,180,41,0.12)', pointerEvents: 'none' }} />

                <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '16px', background: 'rgba(240,180,41,0.15)', marginBottom: '24px' }}>
                  <BookOpen size={32} color="#F0B429" />
                </div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: '800', color: 'white', marginBottom: '16px', lineHeight: 1.3 }}>
                  Founded on a Bold Belief
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, fontSize: '0.95rem', marginBottom: '32px' }}>
                  "Quality tech education should not require a financial gamble. Students should only pay in full after they succeed."
                </p>

                {/* Pillars mini-chips */}
                {['Education Institute', 'HR Consulting'].map((p) => (
                  <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <CheckCircle size={15} color="#F0B429" />
                    <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem', fontWeight: '500' }}>{p}</span>
                  </div>
                ))}
              </div>

              {/* Floating badge */}
              <motion.div
                animate={{ y: [-4, 4, -4] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute',
                  bottom: '-20px',
                  right: '24px',
                  background: 'linear-gradient(135deg, #F0B429, #FF6535)',
                  borderRadius: '14px',
                  padding: '12px 20px',
                  boxShadow: '0 12px 32px rgba(240,180,41,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'white',
                  fontWeight: '800',
                  fontSize: '0.85rem',
                }}
              >
                <Award size={16} />
                India's First
              </motion.div>
            </div>
          </FadeUp>

          {/* Story text */}
          <FadeUp delay={0.15}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(43,34,64,0.06)', border: '1px solid rgba(43,34,64,0.12)', borderRadius: '50px', padding: '5px 16px', marginBottom: '20px', color: '#2b2240', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Our Story
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '900', color: '#2b2240', lineHeight: 1.2, marginBottom: '20px', letterSpacing: '-0.02em' }}>
              Two Services.{' '}
              <span style={{ background: 'linear-gradient(135deg, #2b2240, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                One Mission.
              </span>
            </h2>
            <p style={{ color: '#6b7280', lineHeight: 1.85, fontSize: '1rem', marginBottom: '20px' }}>
              Vista Tech was founded with a simple but powerful idea — that the gap between education and employment was a design flaw, not an inevitability. We set out to fix it.
            </p>
            <p style={{ color: '#6b7280', lineHeight: 1.85, fontSize: '1rem', marginBottom: '28px' }}>
              We became <strong style={{ color: '#2b2240' }}>India's first institute</strong> to offer a <strong style={{ color: '#2b2240' }}>50% Pay After Placement</strong> model. Our two pillars — Education and HR Consulting — work together to create a full career ecosystem: train talent, then connect it with the companies hiring.
            </p>
            {/* Timeline dots */}
            {[
              { year: '2023', event: 'Vista Tech founded with the 50% Pay After Placement model' },
              { year: '2024', event: 'Introduced AI-powered training across all programs' },
              { year: '2025', event: '500+ students placed across 100+ hiring partner companies' },
              { year: '2026', event: 'Expanded into HR Consulting' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: '16px', marginBottom: '14px', alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: '54px', height: '26px', borderRadius: '6px', background: i === 3 ? 'linear-gradient(135deg, #F0B429, #FF6535)' : 'rgba(43,34,64,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: '800', color: i === 3 ? 'white' : '#2b2240', marginTop: '2px' }}>
                  {t.year}
                </div>
                <p style={{ color: '#4b5563', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>{t.event}</p>
              </div>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* ── TWO PILLARS ───────────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: '#f7f6fb' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(43,34,64,0.06)', border: '1px solid rgba(43,34,64,0.12)', borderRadius: '50px', padding: '5px 16px', marginBottom: '16px', color: '#2b2240', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                What We Do
              </div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '900', color: '#2b2240', marginBottom: '12px', letterSpacing: '-0.02em' }}>
                Two Pillars,{' '}
                <span style={{ background: 'linear-gradient(135deg, #2b2240, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  One Ecosystem
                </span>
              </h2>
              <p style={{ color: '#6b7280', fontSize: '1.05rem', maxWidth: '540px', margin: '0 auto' }}>
                Education creates talent. HR connects it with the companies hiring. Vista Tech does both.
              </p>
            </div>
          </FadeUp>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '28px' }}>
            {pillars.map((p, i) => (
              <FadeUp key={p.label} delay={i * 0.12}>
                <motion.div
                  whileHover={{ y: -6, boxShadow: '0 32px 80px rgba(43,34,64,0.16)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  style={{
                    background: 'white',
                    borderRadius: '24px',
                    padding: '36px 32px',
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(43,34,64,0.07)',
                    border: '1px solid rgba(43,34,64,0.06)',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                  }}
                >
                  {/* Top accent bar */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(90deg, ${p.color}, ${p.accent})`, borderRadius: '24px 24px 0 0' }} />

                  <div style={{ display: 'inline-flex', padding: '12px', borderRadius: '14px', background: `${p.color}12`, color: p.color, marginBottom: '20px' }}>
                    {p.icon}
                  </div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: p.accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>{p.label}</div>
                  <h3 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#2b2240', marginBottom: '12px', lineHeight: 1.3 }}>{p.headline}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: '28px' }}>{p.desc}</p>

                  {/* Stat */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(43,34,64,0.08)', paddingTop: '20px' }}>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: '900', color: p.color, lineHeight: 1 }}>{p.stat}</div>
                      <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '3px' }}>{p.statLabel}</div>
                    </div>
                    <Link to={p.link} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', background: `${p.color}10`, color: p.color, fontWeight: '700', fontSize: '0.82rem', textDecoration: 'none' }}>
                      Learn More <ChevronRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT MAKES US DIFFERENT ───────────────────────── */}
      <section style={{
        padding: '100px 24px',
        background: 'linear-gradient(135deg, #0d0b14 0%, #2b2240 60%, #1a1030 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Grid overlay */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <motion.div style={{ position: 'absolute', top: '-120px', right: '-120px', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(240,180,41,0.07)', filter: 'blur(80px)', pointerEvents: 'none' }} animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 8, repeat: Infinity }} />

        <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(240,180,41,0.12)', border: '1px solid rgba(240,180,41,0.25)', borderRadius: '50px', padding: '5px 16px', marginBottom: '16px', color: '#F0B429', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Why Vista Tech
              </div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '900', color: 'white', marginBottom: '12px', letterSpacing: '-0.02em' }}>
                What Makes Us{' '}
                <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Truly Different
                </span>
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.05rem', maxWidth: '520px', margin: '0 auto' }}>
                The only institute in India that combines all six of these in a single program.
              </p>
            </div>
          </FadeUp>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {differentiators.map((d, i) => (
              <FadeUp key={d.title} delay={i * 0.08}>
                <motion.div
                  whileHover={{ scale: 1.02, background: 'rgba(255,255,255,0.09)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '18px',
                    padding: '28px 26px',
                    display: 'flex',
                    gap: '18px',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flexShrink: 0, width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(240,180,41,0.2), rgba(255,101,53,0.15))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F0B429' }}>
                    {d.icon}
                  </div>
                  <div>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'white', marginBottom: '7px', lineHeight: 1.3 }}>{d.title}</h4>
                    <p style={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.65, margin: 0 }}>{d.desc}</p>
                  </div>
                </motion.div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────── */}
      <section style={{ padding: '80px 24px', background: 'white', borderBottom: '1px solid rgba(43,34,64,0.07)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0' }}>
          {stats.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.1}>
              <div style={{ textAlign: 'center', padding: '32px 20px', borderRight: i < stats.length - 1 ? '1px solid rgba(43,34,64,0.08)' : 'none' }}>
                <div style={{ fontSize: 'clamp(2.2rem, 5vw, 3rem)', fontWeight: '900', background: 'linear-gradient(135deg, #2b2240, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: 1, marginBottom: '8px' }}>
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: '0.82rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{s.label}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ── CORE VALUES ───────────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: '#f7f6fb' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(43,34,64,0.06)', border: '1px solid rgba(43,34,64,0.12)', borderRadius: '50px', padding: '5px 16px', marginBottom: '16px', color: '#2b2240', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Core Values
              </div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '900', color: '#2b2240', marginBottom: '12px', letterSpacing: '-0.02em' }}>
                Principles We{' '}
                <span style={{ background: 'linear-gradient(135deg, #2b2240, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Never Compromise
                </span>
              </h2>
              <p style={{ color: '#6b7280', fontSize: '1.05rem', maxWidth: '500px', margin: '0 auto' }}>
                Every decision at Vista Tech flows from these six beliefs.
              </p>
            </div>
          </FadeUp>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {values.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -4, boxShadow: '0 20px 50px rgba(43,34,64,0.12)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                  style={{
                    background: 'white',
                    borderRadius: '18px',
                    padding: '28px 26px',
                    boxShadow: '0 2px 12px rgba(43,34,64,0.06)',
                    border: '1px solid rgba(43,34,64,0.06)',
                    display: 'flex',
                    gap: '18px',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flexShrink: 0, width: '42px', height: '42px', borderRadius: '11px', background: 'rgba(43,34,64,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2b2240' }}>
                    {v.icon}
                  </div>
                  <div>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#2b2240', marginBottom: '6px' }}>{v.title}</h4>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.65, margin: 0 }}>{v.desc}</p>
                  </div>
                </motion.div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: 'white' }}>
        <FadeUp>
          <div style={{
            maxWidth: '820px',
            margin: '0 auto',
            background: 'linear-gradient(135deg, #2b2240 0%, #4e3a69 50%, #2b2240 100%)',
            borderRadius: '28px',
            padding: 'clamp(48px, 7vw, 72px) clamp(32px, 6vw, 64px)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 32px 80px rgba(43,34,64,0.3)',
          }}>
            {/* Orbs */}
            <div style={{ position: 'absolute', top: '-60px', left: '-60px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(240,180,41,0.12)', filter: 'blur(50px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-60px', right: '-60px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,101,53,0.1)', filter: 'blur(50px)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '16px', background: 'rgba(240,180,41,0.15)', marginBottom: '24px' }}>
                <Rocket size={28} color="#F0B429" />
              </div>
              <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: '900', color: 'white', marginBottom: '16px', lineHeight: 1.25, letterSpacing: '-0.02em' }}>
                Ready to Transform Your Career?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '520px', margin: '0 auto 36px' }}>
                Join Vista Tech — India's first 50% Pay After Placement institute. Your first job is our success too.
              </p>
              <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/courses" style={{ padding: '14px 32px', borderRadius: '12px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: 'white', fontWeight: '700', textDecoration: 'none', fontSize: '0.95rem', boxShadow: '0 8px 24px rgba(240,180,41,0.4)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Browse Courses <ChevronRight size={17} />
                </Link>
                <Link to="/contact" style={{ padding: '14px 32px', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.85)', fontWeight: '600', textDecoration: 'none', fontSize: '0.95rem', background: 'rgba(255,255,255,0.06)' }}>
                  Talk to a Counselor
                </Link>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

    </div>
  );
};

export default About;
